import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ConflictError } from '../../api/errors';
import { fieldWorkApi } from '../../api/field-work-api';
import { useAuth } from '../../auth/AuthContext';
import { SearchableSelect, type SearchableSelectOption } from '../../components/SearchableSelect';
import { MAX_NOTE_LENGTH, type FieldWorkRequest } from '../../domain/fieldWorkRequest';
import { handOffCandidates } from './handoff-candidates';

/**
 * Hands a request to someone else with a note for them. The assignee and
 * the note are saved together; the status doesn't change.
 */
export function HandOffDialog({
  request,
  people,
  onClose,
  onDone,
  onReload,
}: {
  request: FieldWorkRequest;
  people: SearchableSelectOption[];
  onClose: () => void;
  onDone: (updated: FieldWorkRequest) => void;
  onReload: () => void;
}) {
  const { user } = useAuth();
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<{ text: string; conflict: boolean } | null>(null);
  const handOff = useMutation({
    mutationFn: () => fieldWorkApi.handOff(request.id, request.version, to, note),
    onSuccess: onDone,
    onError: (failure: unknown) =>
      setError(
        failure instanceof ConflictError
          ? {
              text: 'Someone else changed this request. Reload it and hand it off again.',
              conflict: true,
            }
          : {
              text:
                failure instanceof Error ? failure.message : 'The request could not be handed off.',
              conflict: false,
            },
      ),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    if (!to) {
      setError({ text: 'Choose who takes it over.', conflict: false });
      return;
    }
    if (!note.trim()) {
      setError({
        text: 'Write a note for them: what’s done, what’s next, where things are.',
        conflict: false,
      });
      return;
    }
    handOff.mutate();
  }

  const options = handOffCandidates(people, request.assignee, user?.email);
  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
      <form
        className="confirmation-dialog handoff-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Hand off request"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <div className="drawer-heading">
          <h2>Hand off request</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close hand off dialog"
          >
            ×
          </button>
        </div>
        <p className="field-hint">
          {request.assignee ? `${request.assignee} has it now. ` : 'Nobody has it now. '}
          It moves to the person you choose straight away, with your note, and they get an email
          with the note and a link.
        </p>
        <label>
          Hand off to
          <SearchableSelect
            ariaLabel="Hand off to"
            value={to}
            onChange={setTo}
            options={options}
            placeholder="Choose a person"
          />
        </label>
        <label>
          Note for them (required)
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={4}
            maxLength={MAX_NOTE_LENGTH}
            placeholder="What’s done, what’s next, where things are"
          />
        </label>
        {error ? (
          <div role="alert" className="form-error">
            {error.text}
            {error.conflict ? (
              <button type="button" className="link-button" onClick={onReload}>
                Reload request
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="drawer-actions">
          <button type="button" onClick={onClose}>
            Keep it
          </button>
          <button type="submit" className="primary" disabled={handOff.isPending}>
            {handOff.isPending ? 'Handing off…' : 'Hand off'}
          </button>
        </div>
      </form>
    </div>
  );
}
